export interface OrdersParams {
  startDate: string; // YYYY-MM-DD
  endDate: string; // YYYY-MM-DD
  patientPhone?: string;
  patientName?: string;
  page: number;
  limit?: number; //default 30
}

export enum EOrderSearchType {
  PATIENT_NAME = 'patinetName',
  PATIENT_PHONE = 'patinetPhone'
}

export enum ERequestedStatusType {
  READY = 'ready',
  WAITING_PAYMENT = 'waiting_payment',
  CANCEL = 'cancel'
}

export enum ERequestedSortType {
  CREATED_AT_DESC = 'created_at_desc',
  CREATED_AT_ASC = 'created_at_asc'
}

export interface RequestedOrdersParams extends OrdersParams {
  status: ERequestedStatusType | null;
  sort: ERequestedSortType | null;
}

export enum EPreparingStatusType {
  REQUEST_PICKUP = 'request_pickup',
  PICKUP_CHECK = 'pickup_check'
}

export enum EPreparingSortType {
  PAID_AT_DESC = 'paid_at_desc',
  PAID_AT_ASC = 'paid_at_asc'
}

export interface PreparingOrdersParams extends OrdersParams {
  status: EPreparingStatusType | null;
  sort: EPreparingSortType | null;
}

export interface SettlementsParams {
  from: string;
  to: string;
  phone?: string;
  username?: string;
}

export interface OrdersSettlementsParams extends SettlementsParams {
  page: number;
  limit: number;
}

export interface Order {
  agencyType: string;
  callAt: Date | null;
  cancelBy: null;
  cancelReason: null;
  checkedAt: Date | null;
  createdAt: Date | null;
  deliveryAt: Date | null;
  detailAddress: string;
  diagnosisId: number;
  dropAt: Date | null;
  id: number;
  isFree: false;
  jibunAddress: string;
  jobId: null;
  latitude: number;
  longitude: number;
  orderId: string;
  orderStatus: string;
  orderType: string;
  paidAt: Date | null;
  payStatus: string;
  pharmacyId: number;
  phone: string;
  prescriptions: Array<Prescription>;
  price: number;
  receivedAt: Date | null;
  roadAddress: string;
  sendAt: Date | null;
  threadId: string;
  updatedAt: Date | null;
  useAlternative: false;
  userId: number;
  username: string;
  vroongDeliveryId: null;
  isCoronaDiagnosis: boolean;
  diagnosisExternalId: string;
  diagnosis: Diagnosis;
  inflowType: string;
  medicineFormTotalPrice?: number;
  shipmentInfo: ShipmentInfo | null;
  doctorInfo?: DoctorInfo;
  hasAdditionalOrder?: boolean;
  priceSetAt: Date | null;
  isPrescriptionRevised?: boolean;
  isImmediatePayment: boolean;
  orderMedicineRequestInfo?: {
    isAlternative: boolean;
    requestedMedicineId: number;
    requestedMedicineName: string;
  };
  alternativeLog?: string;
}

export type ShipmentInfo = {
  shippingCompanyName: string;
  customerServiceTel?: string;
  operationInfo?: Array<string>;
  trackingId?: string;
  riderName?: string;
  riderPhone?: string;
};

export type DoctorInfo = {
  id: number;
  name: string;
  hospitalName: string;
  hospitalTel: string;
  phone: string;
};

export enum ERelationType {
  ME = 'me',
  PARENTS = 'parents',
  CHILDREN = 'children',
  SPOUSE = 'SPOUSE'
}

export const RELATION_EXPRESSION = {
  [ERelationType.ME]: '본인',
  [ERelationType.PARENTS]: '부모',
  [ERelationType.CHILDREN]: '자녀',
  [ERelationType.SPOUSE]: '배우자'
};

// server-side
type DiagnosisType = 'department' | 'treatment' | 'medicine';

export interface DiagnosisForm {
  id: number;
  patientName: string;
  patientPhone: string;
  patientSsn: string;
  patientGender: string;
  patientBirth: string;
  patientRelation: ERelationType;
  symptom: string;
  isVisit: boolean;
  agreementAt: Date;
  requestForVideoCall: boolean;
  diagnosisType: DiagnosisType;
  diagnosisTypeId: number;
  createdAt: Date;
  updatedAt: Date;
  diagnosisCategoryType: string;
}

export interface Diagnosis {
  id: number;
  receiptId: string;
  doctorId: number;
  userId: number;
  price: number;
  status: string;
  doneAt: Date;
  payStatus: string;
  paidAt: Date;
  paymentId: number;
  visible: boolean;
  createdAt: Date;
  updatedAt: Date;
  deletedAt?: any;
  scheduledAt: Date;
  scheduleCount: number;
  startedAt: Date;
  reviewId?: any;
  diagnosisForm: DiagnosisForm;
}

export interface Prescription {
  filename: string;
  originalname: string;
  prescriptionId: number;
  contents?: string;
  reasonTypeDisplayName?: string;
}

export interface Pageable {
  limit: number;
  offset: number;
  pageCount: number;
  totalCount: number;
}

export interface OrdersResponse {
  pageable: Pageable;
  list: Array<Order>;
}

export interface OrderSetPriceParams {
  orderId: string;
  useAlternative: boolean;
  price: number;
  isFree: boolean;
}

export interface OrderCancelByPharmParams {
  orderId: string;
  cancelReason: string;
}

export interface OrderPickupCallParams {
  orderId: string;
  pickupWishMinutes?: number;
  alternativeReason?: string;
}
